// Iter86b — Admin · Logistics Customer Categories.
//
// Lists customer categories and lets the admin toggle the `is_key_account`
// flag (drives the Key Account Dashboard) and add/archive entries.
import React, { useState } from "react";
import { Plus, Crown } from "lucide-react";
import { useLogisticsCategories, useCreateLogisticsCategory, usePatchLogisticsCategory } from "../api/logistics";

export default function AdminLogisticsCategories() {
  const q = useLogisticsCategories();
  const create = useCreateLogisticsCategory();
  const patch = usePatchLogisticsCategory();
  const [newName, setNewName] = useState("");
  const items = (q.data?.items || []).filter((c) => !c.archived);

  return (
    <div className="space-y-4" data-testid="admin-logistics-categories">
      <header>
        <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 22, color: "var(--text-primary)" }}>Customer Categories</h1>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>Toggle Key Account flag to surface in the Key Account Dashboard</div>
      </header>

      <div className="rounded-md p-3" style={card}>
        <div className="flex gap-2">
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name" className="px-2 py-1.5 rounded outline-none flex-1" style={input} data-testid="admin-lc-new-name" />
          <button
            type="button"
            onClick={() => { if (newName.trim()) { create.mutate({ name: newName.trim() }, { onSuccess: () => setNewName("") }); } }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded"
            style={btnPrimary}
            data-testid="admin-lc-new-save"
          >
            <Plus size={13} /> ADD
          </button>
        </div>
      </div>

      <div className="rounded-md overflow-hidden" style={card}>
        <div className="grid items-center px-3 py-2" style={{ gridTemplateColumns: "1.6fr 1fr 1fr", background: "var(--bg-elevated)", borderBottom: "1px solid var(--border-strong)" }}>
          {["Name", "Key Account", "Updated"].map((h) => (
            <div key={h} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>{h}</div>
          ))}
        </div>
        {items.map((c) => (
          <div key={c.id} className="grid items-center px-3 py-2" style={{ gridTemplateColumns: "1.6fr 1fr 1fr", borderBottom: "1px solid var(--border-soft)" }} data-testid={`admin-lc-row-${c.id}`}>
            <div className="flex items-center gap-2">
              {c.is_key_account && <Crown size={14} style={{ color: "#b7770d" }} />}
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-primary)" }}>{c.name}</span>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!c.is_key_account} onChange={(e) => patch.mutate({ id: c.id, body: { is_key_account: e.target.checked } })} data-testid={`admin-lc-key-account-${c.id}`} />
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: c.is_key_account ? "#b7770d" : "var(--text-dimmer)" }}>{c.is_key_account ? "Key Account" : "Standard"}</span>
            </label>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)" }}>{(c.updated_at || c.created_at || "").slice(0, 16)}</span>
          </div>
        ))}
        {items.length === 0 && <div className="px-3 py-6 text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)" }}>No categories yet</div>}
      </div>
    </div>
  );
}

const card = { background: "var(--bg-surface)", border: "1px solid var(--border-strong)" };
const input = { background: "var(--bg-elevated)", border: "1px solid var(--border-strong)", color: "var(--text-primary)", fontFamily: "'DM Mono', monospace", fontSize: 11 };
const btnPrimary = { background: "#185FA5", border: "1px solid #0d3f78", color: "#ffffff", fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: "0.4px", cursor: "pointer" };
